import React, { useState } from "react";

import { useAppSelector, useHandlePageNavigate } from "../../hooks/hooks";
import {
  selectListStatus,
  selectReportLists,
} from "../../features/reports/reportsSlice";

import {
  AnchorLink,
  CollapsableItemList,
  Container,
  ContainerLineItem,
  Layout,
  Padding,
  Text,
} from "@kidslivesafe/sc-react";

import ListLineItem from "../../components/UIComponents/ListLineItem/ListLineItem";
import ListModal, {
  ListModalMode,
} from "../../components/UIComponents/ListModal/ListModal";
import { parseDate } from "../../utils";
import { CombinedListEntry } from "../../features/reports/reportTypes";
import LoadingContainerLineItem from "../../components/UIComponents/LoadingContainerLineItem/LoadingContainerLineItem";

const ListDashboardContainer: React.FC = () => {
  const [mode, setMode] = useState<ListModalMode>("remove-report");
  const [showListModal, setShowListModal] = useState<boolean>(false);
  const [savedReportsList, setSavedReportsList] = useState<any>();
  const [savedReportId, setSavedReportId] = useState<number>(0);

  const pageNavigate = useHandlePageNavigate();

  const handleClick = () => {
    pageNavigate("/lists");
  };

  const lists = useAppSelector(selectReportLists);

  const renderFavoritesList = () => {
    const favoritesList = lists?.filter(list => list.listName === "Favorites");
    return (
      <>
        {favoritesList && favoritesList.length > 0 ? (
          favoritesList.map(list =>
            list ? (
              <ContainerLineItem
                lineItemHeader={<Text tag="h3">{list.listName}</Text>}
                lineItemSubHeader={
                  list.lastActivityDate
                    ? `Modified ${parseDate(list.lastActivityDate, "MM/DD/YYYY")}`
                    : list.dateCreated
                }
                lineItemSubHeaderLine2={
                  list.savedReports?.length
                    ? `${list.savedReports.length} Reports`
                    : "0 Reports"
                }
                key={list.savedReportsListId}
              >
                <CollapsableItemList>
                  {list.savedReports?.length ? (
                    list.savedReports.map((report, index) =>
                      report ? (
                        <Padding
                          space="md"
                          key={report.id}
                          top={index > 0 ? "md" : "none"}
                        >
                          <ListLineItem
                            report={report}
                            onClick={() => handleRemoveItem(report)}
                            noPadding={true}
                          />
                        </Padding>
                      ) : null,
                    )
                  ) : (
                    <Padding space="md">
                      <Text size="xs">
                        Your Favorites list is empty. Save any report to this
                        list from the report page.
                      </Text>
                    </Padding>
                  )}
                </CollapsableItemList>
              </ContainerLineItem>
            ) : null,
          )
        ) : (
          <ContainerLineItem
            lineItemHeader={<Text tag="h3">Favorites</Text>}
            key="favorites"
            lineItemSubHeader="0 Reports"
          >
            <Padding space="md" top="none">
              <Text size="xs">
                Your Favorites list is empty. Save any report to this list from
                the report page.
              </Text>
            </Padding>
          </ContainerLineItem>
        )}
      </>
    );
  };

  const setToggleModal = () => {
    setShowListModal((prevState: any) => !prevState);
  };

  const listStatus = useAppSelector(selectListStatus);
  const loading = listStatus === "loading";

  const handleRemoveItem = async (list: CombinedListEntry) => {
    const selectedList = lists?.find(
      item => item.savedReportsListId === list.savedReportsListId,
    );
    if (selectedList) {
      setSavedReportsList(selectedList);
    }
    setSavedReportId(list.id);
    setMode("remove-report");
    setToggleModal();
  };

  return (
    <>
      <Padding left="md" right="md">
        <Container
          containerTitle="Favorites and Lists"
          containerId="dashboard__lists__container"
          tooltipContent="Easily save and organize reports into custom-named lists directly from report pages."
        >
          <CollapsableItemList numberToShow={5}>
            {loading ? (
              Array(5)
                .fill(null)
                .map((_, index) => (
                  <LoadingContainerLineItem
                    lineItemHeader
                    lineItemSubHeader
                    lineItemDetails
                    key={index}
                  />
                ))
            ) : lists && lists.length > 0 ? (
              <CollapsableItemList>
                {renderFavoritesList()}
                {lists
                  .filter(list => list.listName !== "Favorites")
                  .sort((a, b) => {
                    if (a.lastActivityDate > b.lastActivityDate) {
                      return -1;
                    }
                    if (a.lastActivityDate < b.lastActivityDate) {
                      return 1;
                    }
                    return 0;
                  })
                  .slice(0, 4)
                  .map(list =>
                    list ? (
                      <ContainerLineItem
                        lineItemHeader={
                          <Layout
                            type="flex"
                            flexProps={{ alignItems: "center", gap: "xxs" }}
                          >
                            <Text tag="h3">{list.listName}</Text>
                            <AnchorLink
                              size="xxs"
                              color="primary"
                              role="button"
                              hoverUnderline
                              handleClick={() => {
                                setMode("rename-list");
                                setSavedReportsList(list);
                                setToggleModal();
                              }}
                            >
                              (rename)
                            </AnchorLink>
                          </Layout>
                        }
                        lineItemSubHeader={
                          list.lastActivityDate
                            ? `Created ${parseDate(list.dateCreated, "MM/DD/YYYY")} | Modified ${parseDate(list.lastActivityDate, "MM/DD/YYYY")}`
                            : list.dateCreated
                        }
                        lineItemSubHeaderLine2={
                          list.savedReports?.length
                            ? `${list.savedReports.length} Reports`
                            : "0 Reports"
                        }
                        key={list.savedReportsListId}
                      >
                        <CollapsableItemList numberToShow={100}>
                          {loading ? (
                            Array(5)
                              .fill(null)
                              .map((_, index) => (
                                <ListLineItem
                                  report={undefined}
                                  loading={loading}
                                  loadingKey={index}
                                  key={index}
                                />
                              ))
                          ) : list.savedReports?.length ? (
                            list.savedReports.map((report, index) =>
                              report ? (
                                <Padding
                                  space="md"
                                  key={report.id}
                                  top={index > 0 ? "md" : "none"}
                                >
                                  <ListLineItem
                                    report={report}
                                    loadingKey={report.id}
                                    onClick={() => handleRemoveItem(report)}
                                    noPadding={true}
                                  />
                                </Padding>
                              ) : null,
                            )
                          ) : (
                            <Padding space="md">
                              <Text>No Lists</Text>
                            </Padding>
                          )}
                        </CollapsableItemList>
                      </ContainerLineItem>
                    ) : null,
                  )}
              </CollapsableItemList>
            ) : (
              renderFavoritesList()
            )}
          </CollapsableItemList>
          <Padding space="md" top="none">
            <Layout type="flex" flexProps={{ justifyContent: "center" }}>
              <AnchorLink
                handleClick={handleClick}
                role="button"
                hoverUnderline
                size="xs"
                fontWeight="semibold"
              >
                View All Lists
              </AnchorLink>
            </Layout>
          </Padding>
        </Container>
      </Padding>
      <ListModal
        modalMode={mode}
        setModalMode={setMode}
        displayModal={showListModal}
        setDisplayModal={setShowListModal}
        reportId={savedReportId}
        list={savedReportsList}
      />
    </>
  );
};

export default ListDashboardContainer;
